import React, { useCallback, useEffect, useRef, useState } from "react";
import { sortBy } from "lodash";

import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, alpha } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { PenPortraitInsightVariable } from "@/types";
import { toIndex, toPercentage, whiteOverlay } from "@/utils";

interface TopBrandsCardProps {
  variables: PenPortraitInsightVariable[];
  score: string;
  levels: number[];
}

const TopBrandsCard: React.FC<TopBrandsCardProps> = ({
  variables,
  score,
  levels,
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  function useElementWidth() {
    const [width, setWidth] = useState<number>(275);
    const observerRef = useRef<ResizeObserver | null>(null);

    const ref = useCallback((node: HTMLElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();

      if (node) {
        const observer = new ResizeObserver(([entry]) => {
          setWidth(entry.contentRect.width);
        });

        observer.observe(node);
        observerRef.current = observer;
      }
    }, []);

    // Cleanup
    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, []);

    return [ref, width] as const;
  }

  // Get the size of the card
  const [cardRef, cardWidth] = useElementWidth();

  // Calculate the size of the images
  const numImages = cardWidth > 264 ? 6 : 4
  const imageSize = Math.min(Math.max(Math.floor((cardWidth - 32) / numImages) - 8, 32), 100);

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      )
      .filter((x) => levels.includes(x.tag_level));
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables, levels]);

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Box sx={{ py: 1, px: 2 }}>
        <Typography variant="body1" gutterBottom >
          Most Relevant Attributes
        </Typography>
        <Grid container spacing={2}>
          {scores.slice(0, numImages).map((variable) => (
            <Grid size={4} key={`top-${variable.tag_id}`}>
              <HelpBox
                title={`${variable.tag_name} (${formatFunc(
                  Number(variable[score as keyof PenPortraitInsightVariable]),
                )})`}
              >
                <Stack direction="row" spacing={1} alignItems="center" py="6px">
                  <Box
                    component="img"
                    src={variable.image}
                    alt={variable.tag_name}
                    height={60}
                    width={60}
                    display="block"
                    style={{
                      borderRadius: 5,
                      padding: variable.tag_type === "Interests" ? 6 : undefined,
                      backgroundColor: theme.palette.background.paper,
                      backgroundImage: variable.tag_type === "Interests" ? whiteOverlay(0.1) : undefined,
                    }}
                  />
                  <Typography
                    sx={{
                      typography: { xs: "caption", xl: "body2" },
                      display: { xs: "none", lg: "block"},
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {variable.tag_name}
                  </Typography>
                </Stack>
              </HelpBox>
            </Grid>
          ))}
        </Grid>
        </Box>
    );
  }
};

export default TopBrandsCard;
